/**
 * 四象限图数据 Hook
 */
import { useState, useEffect, useMemo } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getSubtopicQuadrant } from '@op/services/weeklyReport';
import type {
  ISubtopicQuadrantItem,
  ISubtopicQuadrantResponse,
} from '@op/services/weeklyReport';
import { calculateQuantile, getQuadrant } from '../utils/calculations';
import { DBO_V } from '@op/utils/format';

// 处理后的数据点类型（包含计算字段）
export interface IProcessedQuadrantItem extends ISubtopicQuadrantItem {
  change_rate: number;
  quadrant: 1 | 2 | 3 | 4;
}

interface IUseQuadrantDataProps {
  filter: any;
}

interface IUseQuadrantDataResult {
  loading: boolean;
  empty: boolean;
  processedData: IProcessedQuadrantItem[];
  thresholds: {
    x_axis: number;
    y_axis: number;
  };
  linkTime: {
    start_time: string;
    end_time: string;
  } | null;
  categoryList: string[];
  categoryCnMap: Map<string, string>;
}

/**
 * 四象限图数据 Hook
 * @param filter - 筛选条件（需包含 edition_unified_id, start_time, end_time）
 */
export function useQuadrantData({
  filter,
}: IUseQuadrantDataProps): IUseQuadrantDataResult {
  const { loading, wrapperCancelRequest } = useCancelRequestHook();
  const [data, setData] = useState<ISubtopicQuadrantResponse | null>(null);

  // 请求数据
  useEffect(() => {
    if (filter?.start_time && filter?.end_time && filter?.edition_unified_id) {
      wrapperCancelRequest(getSubtopicQuadrant, filter)
        .then(res => setData((res as ISubtopicQuadrantResponse) || null))
        .catch(() => {});
    }
  }, [filter]);
 

  const { chartData, linkTime } = useMemo(() => {
    return {
      chartData: data?.chart_data || [],
      linkTime: data?.link_time || null,
    };
  }, [data]);

  const empty = useMemo(
    () => !loading && chartData.length === 0,
    [loading, chartData],
  );

  // 处理数据：计算环比变化率、分位值、象限
  const { processedData, thresholds } = useMemo(() => {
    if (chartData.length === 0) {
      return { processedData: [], thresholds: { x_axis: 0, y_axis: 0 } };
    }

    // 计算环比变化率（使用 DBO_V.qoq，返回百分比需除以 100 转为小数）
    const dataWithChangeRate = chartData.map(item => ({
      ...item,
      change_rate:
        DBO_V.qoq(item.mentions, item.previous_period_mentions) / 100,
    }));

    // 计算分位值
    const mentionsValues = dataWithChangeRate.map(d => d.mentions);
    const intensityValues = dataWithChangeRate.map(
      d => d.avg_engagement_intensity,
    );
    const xAxisThreshold = calculateQuantile(mentionsValues, 0.6); // P60
    const yAxisThreshold = calculateQuantile(intensityValues, 0.6); // P50

    // 计算象限
    const processedData: IProcessedQuadrantItem[] = dataWithChangeRate.map(
      item => ({
        ...item,
        quadrant: getQuadrant(
          item.mentions,
          item.avg_engagement_intensity,
          xAxisThreshold,
          yAxisThreshold,
        ),
      }),
    );

    return {
      processedData,
      thresholds: { x_axis: xAxisThreshold, y_axis: yAxisThreshold },
    };
  }, [chartData]);

  // 获取所有 category 列表及其中文名映射
  const { categoryList, categoryCnMap } = useMemo(() => {
    const categorySet = new Set<string>();
    const cnMap = new Map<string, string>();
    processedData.forEach((d) => {
      categorySet.add(d.category);
      if (d.category_cn) {
        cnMap.set(d.category, d.category_cn);
      }
    });
    return {
      categoryList: Array.from(categorySet),
      categoryCnMap: cnMap,
    };
  }, [processedData]);

  return {
    loading,
    empty,
    processedData,
    thresholds,
    linkTime,
    categoryList,
    categoryCnMap,
  };
}

export default useQuadrantData;
